import type { SfxName } from '../content/types';
import { PLAYLISTS, pickMood, Shuffler, TRACKS, type Mood, type MoodInput, type TrackId } from './music';

export interface LoopHandle {
  stop(): void;
  /** 0..1 intensity (e.g. engine RPM). */
  set(value: number): void;
}

/** Where you are and what the weather's doing, for the ambience and the music. */
export interface SoundScene extends MoodInput {
  /** Inside a room (your trailer, a shop, a neighbor's place). */
  indoors: boolean;
}

/** Sound effects and loops, recorded and designed from Logic Pro's sound library (tools/audio). */
type SampleName = Exclude<SfxName, 'tornado'> | 'thunder' | 'voice';

export type BedId = 'day' | 'night' | 'swamp' | 'rain' | 'wind' | 'bar' | 'fair' | 'indoor';
export const BEDS: readonly BedId[] = ['day', 'night', 'swamp', 'rain', 'wind', 'bar', 'fair', 'indoor'];

/** Every sound effect has a sample: the compiler checks this list against SfxName. */
const SAMPLE_SET: Record<SampleName, true> = {
  canOpen: true,
  gulp: true,
  burp: true,
  cash: true,
  clank: true,
  boing: true,
  fail: true,
  airhorn: true,
  pickup: true,
  bark: true,
  yip: true,
  siren: true,
  whoop: true,
  crunch: true,
  splash: true,
  thud: true,
  door: true,
  zap: true,
  scratch: true,
  ding: true,
  error: true,
  crowd: true,
  horn: true,
  snore: true,
  phone: true,
  boom: true,
  whistle: true,
  semiHorn: true,
  gunshot: true,
  ting: true,
  lever: true,
  thunk: true,
  ufo: true,
  moo: true,
  thunder: true,
  voice: true,
};
export const SAMPLES = Object.keys(SAMPLE_SET) as SampleName[];

/** Per-sound trims so nothing jumps out of the mix. */
const GAIN: Partial<Record<SampleName, number>> = {
  scratch: 0.45,
  siren: 0.8,
  crowd: 0.8,
  snore: 0.7,
  whoop: 0.7,
  ufo: 0.85,
  pickup: 0.75,
  clank: 0.85,
  ding: 0.8,
};

/** Foley that sounds fake when it repeats exactly: nudge the pitch a little each time. */
const VARY = new Set<SampleName>([
  'clank',
  'thud',
  'scratch',
  'crunch',
  'bark',
  'yip',
  'thunk',
  'ting',
  'splash',
  'door',
  'whoop',
  'boing',
  'gulp',
  'canOpen',
]);

export const STINGERS = { win: 'music/win.mp3', evicted: 'music/evicted.mp3' } as const;
type StingerName = keyof typeof STINGERS;

/** Relative to the page, so it works on GitHub Pages project sites. */
const BASE = `${import.meta.env.BASE_URL}audio/`;
const MUSIC_FADE = 1.6;

interface Clip {
  buffer: AudioBuffer;
  /** Where the sound starts and ends, past the MP3 encoder's silent padding. */
  start: number;
  end: number;
}

interface Deck {
  el: HTMLAudioElement;
  gain: GainNode;
  track: TrackId | null;
}

interface BedVoice {
  gain: GainNode;
  src: AudioBufferSourceNode | null;
}

/**
 * The game's audio: sound effects, ambience beds and the soundtrack. Everything was made in
 * Logic Pro and ships as MP3 in public/audio. Effects are decoded into buffers; songs stream
 * through two <audio> decks that crossfade. The context is created on the first user gesture
 * (browser autoplay rules).
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private musicBus!: GainNode;
  private musicTone!: BiquadFilterNode;
  private ambienceBus!: GainNode;
  private readonly clips = new Map<string, Clip>();
  private readonly loading = new Map<string, Promise<Clip | null>>();
  private readonly beds = new Map<BedId, BedVoice>();
  private decks: Deck[] = [];
  private active = 0;
  private readonly shuffler = new Shuffler();
  private mood: Mood | null = null;
  /** Seconds until the next song starts (between songs, or after the music comes back on). */
  private gap = -1;
  private tornado = 0;
  private thunderTimer = 8;
  private musicVolume = 0.55;
  private sfxVolume = 0.8;
  private musicOn = true;
  private decksUnlocked = false;

  get ready(): boolean {
    return this.ctx !== null;
  }

  /** Sound is actually coming out (the browser has allowed it). */
  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /** Call from a user gesture. Safe to call repeatedly. */
  init(): void {
    if (this.ctx) {
      void this.ctx.resume();
      this.unlockDecks();
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0.95;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -12;
    comp.ratio.value = 3;
    this.master.connect(comp).connect(ctx.destination);
    this.sfxBus = ctx.createGain();
    this.musicBus = ctx.createGain();
    this.ambienceBus = ctx.createGain();
    this.musicTone = ctx.createBiquadFilter();
    this.musicTone.type = 'lowpass';
    this.musicTone.frequency.value = 20000;
    this.sfxBus.gain.value = this.sfxVolume;
    this.musicBus.gain.value = this.musicOn ? this.musicVolume : 0;
    this.ambienceBus.gain.value = 0.9;
    this.sfxBus.connect(this.master);
    this.musicTone.connect(this.musicBus).connect(this.master);
    this.ambienceBus.connect(this.master);

    for (const name of SAMPLES) void this.load(`sfx/${name}.mp3`);
    this.decks = [this.deck(), this.deck()];
    this.unlockDecks();
    this.mood = null;
  }

  /** iOS only lets an <audio> element start on its own after it has played inside a gesture. */
  private unlockDecks(): void {
    if (this.decksUnlocked) return;
    for (const d of this.decks) {
      if (d.track) continue;
      d.el.muted = true;
      d.el.src = `${BASE}sfx/voice.mp3`;
      d.el
        .play()
        .then(() => {
          this.decksUnlocked = true;
          if (!d.track) d.el.pause();
        })
        .catch(() => undefined)
        .finally(() => (d.el.muted = false));
    }
  }

  setVolumes(music: number, sfx: number): void {
    this.musicVolume = music;
    this.sfxVolume = sfx;
    if (!this.ctx) return;
    this.musicBus.gain.setTargetAtTime(this.musicOn ? music : 0, this.ctx.currentTime, 0.1);
    this.sfxBus.gain.setTargetAtTime(sfx, this.ctx.currentTime, 0.1);
  }

  toggleMusic(): boolean {
    this.musicOn = !this.musicOn;
    if (this.ctx) {
      this.musicBus.gain.setTargetAtTime(this.musicOn ? this.musicVolume : 0, this.ctx.currentTime, 0.15);
      if (this.musicOn) {
        this.mood = null;
      } else {
        const d = this.decks[this.active];
        window.setTimeout(() => {
          if (!this.musicOn) d?.el.pause();
        }, 600);
      }
    }
    return this.musicOn;
  }

  get musicEnabled(): boolean {
    return this.musicOn;
  }

  // --- loading -------------------------------------------------------------------------------

  private load(path: string): Promise<Clip | null> {
    const ctx = this.ctx;
    if (!ctx) return Promise.resolve(null);
    let p = this.loading.get(path);
    if (!p) {
      p = fetch(BASE + path)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${r.status} ${path}`))))
        .then((data) => ctx.decodeAudioData(data))
        .then((buffer) => {
          const clip = { buffer, ...audibleSpan(buffer) };
          this.clips.set(path, clip);
          return clip;
        })
        .catch((err: unknown) => {
          console.warn('audio: could not load', path, err);
          return null;
        });
      this.loading.set(path, p);
    }
    return p;
  }

  private clip(name: SampleName): Clip | undefined {
    return this.clips.get(`sfx/${name}.mp3`);
  }

  private start(clip: Clip, dest: AudioNode, gain: number, rate = 1, when = 0): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = clip.buffer;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(dest);
    src.start(when || ctx.currentTime, clip.start, clip.end - clip.start);
    return src;
  }

  // --- sound effects -------------------------------------------------------------------------

  /** Dialogue "voice": a plucked note, pitched to the character. */
  blip(pitch: number): void {
    const ctx = this.ctx;
    const clip = ctx && this.clip('voice');
    if (!ctx || !clip) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = clip.buffer;
    src.playbackRate.value = Math.min(1.7, Math.max(0.55, pitch / 300)) * (0.93 + Math.random() * 0.14);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.32, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    src.connect(g).connect(this.sfxBus);
    src.start(t, clip.start);
    src.stop(t + 0.22);
  }

  /** `pitch` speeds the sample up (or down): a car horn at 1.8 is a scooter's "meep." */
  play(name: SfxName, pitch = 1): void {
    if (!this.ctx) return;
    if (name === 'tornado') {
      this.tornado = 55;
      void this.load('amb/wind.mp3');
      return;
    }
    const clip = this.clip(name);
    if (!clip) return;
    const rate = (VARY.has(name) ? 0.95 + Math.random() * 0.1 : 1) * pitch;
    this.start(clip, this.sfxBus, GAIN[name] ?? 1, rate);
  }

  /** A plain electronic beep, like a backup alarm. */
  beep(freq = 1150, seconds = 0.16): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.07, t + 0.01);
    g.gain.setValueAtTime(0.07, t + seconds - 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + seconds);
    osc.connect(g).connect(this.sfxBus);
    osc.start(t);
    osc.stop(t + seconds + 0.02);
  }

  /** A short musical cue over everything else: the double wide, or the eviction notice. */
  stinger(name: StingerName): void {
    if (!this.ctx) return;
    void this.load(STINGERS[name]).then((clip) => {
      if (clip && this.ctx) this.start(clip, this.sfxBus, 0.9);
    });
  }

  /**
   * Continuous sounds: the push mower, the Camaro's V8, and the Rascal's electric motor
   * (the mower, sped way up and muffled down to a whine).
   */
  loop(kind: 'mower' | 'engine' | 'scooter'): LoopHandle {
    const ctx = this.ctx;
    if (!ctx) return { stop: () => undefined, set: () => undefined };
    const engine = kind === 'engine';
    const scooter = kind === 'scooter';
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = engine ? 1400 : scooter ? 1800 : 6000;
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    g.gain.setTargetAtTime(engine ? 0.55 : scooter ? 0.12 : 0.5, ctx.currentTime, 0.12);
    filter.connect(g).connect(this.sfxBus);
    let src: AudioBufferSourceNode | null = null;
    let stopped = false;
    let rate = scooter ? 1.6 : 1;
    void this.load(`amb/${scooter ? 'mower' : kind}.mp3`).then((clip) => {
      if (!clip || stopped) return;
      src = ctx.createBufferSource();
      src.buffer = clip.buffer;
      src.loop = true;
      src.loopStart = clip.start;
      src.loopEnd = clip.end;
      src.playbackRate.value = rate;
      src.connect(filter);
      src.start(ctx.currentTime, clip.start);
    });
    return {
      stop: () => {
        if (stopped) return;
        stopped = true;
        const now = ctx.currentTime;
        g.gain.cancelScheduledValues(now);
        g.gain.setTargetAtTime(0.0001, now, 0.08);
        src?.stop(now + 0.4);
      },
      set: (v: number) => {
        const now = ctx.currentTime;
        rate = engine ? 0.85 + v * 1.35 : scooter ? 1.5 + v * 1.1 : 0.95 + v * 0.3;
        src?.playbackRate.setTargetAtTime(rate, now, 0.08);
        filter.frequency.setTargetAtTime(
          engine ? 1400 + v * 4200 : scooter ? 1500 + v * 2200 : 6000,
          now,
          0.08,
        );
        g.gain.setTargetAtTime(engine ? 0.5 + v * 0.35 : scooter ? 0.05 + v * 0.14 : 0.5, now, 0.1);
      },
    };
  }

  // --- the frame update: ambience and music ----------------------------------------------------

  /** Call every frame. */
  update(dt: number, scene: SoundScene): void {
    if (!this.ctx) return;
    this.updateAmbience(dt, scene);
    this.updateMusic(dt, scene);
  }

  private updateAmbience(dt: number, s: SoundScene): void {
    const ctx = this.ctx!;
    if (this.tornado > 0) this.tornado = Math.max(0, this.tornado - dt);
    // Tornado: the wind builds for 6 seconds, roars, then dies off over the last 15.
    const twister = this.tornado > 0 ? Math.min(1, (55 - this.tornado) / 6, this.tornado / 15) : 0;
    const out = s.indoors || s.bar ? 0.12 : 1;
    const calm = 1 - s.storm;
    const swamp = Math.min(1, s.murk * 1.6);
    const target: Record<BedId, number> = {
      day: out * calm * (1 - s.night) * (1 - swamp * 0.8) * (s.fair ? 0.4 : 1),
      night: out * calm * s.night * (1 - swamp * 0.7),
      swamp: out * swamp * (1 - s.storm * 0.6),
      fair: out * calm * (s.fair ? 0.9 : 0),
      rain: out * s.storm,
      wind: Math.max(out * s.storm * 0.5, twister),
      bar: s.bar ? 1 : 0,
      indoor: s.indoors && !s.bar ? 0.9 : 0,
    };
    if (s.mode === 'title') {
      for (const id of BEDS) target[id] *= 0.5;
    }
    for (const id of BEDS) this.bed(id, target[id]);

    if (s.storm > 0.55 && !s.indoors && s.mode === 'play') {
      this.thunderTimer -= dt;
      if (this.thunderTimer <= 0) {
        this.thunderTimer = 7 + Math.random() * 12;
        const clip = this.clip('thunder');
        if (clip)
          this.start(clip, this.ambienceBus, 0.5 + s.storm * 0.5, 0.9 + Math.random() * 0.2, ctx.currentTime);
      }
    }
  }

  private bed(id: BedId, level: number): void {
    const ctx = this.ctx!;
    let v = this.beds.get(id);
    if (!v) {
      if (level < 0.01) return;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.ambienceBus);
      v = { gain, src: null };
      this.beds.set(id, v);
      const voice = v;
      void this.load(`amb/${id}.mp3`).then((clip) => {
        if (!clip) return;
        const src = ctx.createBufferSource();
        src.buffer = clip.buffer;
        src.loop = true;
        src.loopStart = clip.start;
        src.loopEnd = clip.end;
        src.connect(voice.gain);
        // Start each bed somewhere different so the loops never line up the same way twice.
        src.start(ctx.currentTime, clip.start + Math.random() * (clip.end - clip.start));
        voice.src = src;
      });
    }
    v.gain.gain.setTargetAtTime(level, ctx.currentTime, 0.8);
  }

  private deck(): Deck {
    const ctx = this.ctx!;
    const el = new Audio();
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    const gain = ctx.createGain();
    gain.gain.value = 0;
    ctx.createMediaElementSource(el).connect(gain).connect(this.musicTone);
    const deck: Deck = { el, gain, track: null };
    el.addEventListener('ended', () => {
      if (this.decks[this.active] === deck && !el.loop) {
        deck.track = null;
        this.gap = 2.5 + Math.random() * 3.5;
      }
    });
    return deck;
  }

  private updateMusic(dt: number, s: SoundScene): void {
    const ctx = this.ctx!;
    // In your trailer or a shop the music sounds like it's coming through the wall.
    const muffled = s.indoors && !s.bar && s.mode === 'play';
    this.musicTone.frequency.setTargetAtTime(muffled ? 1100 : 20000, ctx.currentTime, 0.3);

    if (!this.musicOn) return;
    const mood = pickMood(s);
    if (mood !== this.mood) {
      this.mood = mood;
      const current = this.decks[this.active]?.track;
      if (mood === 'quiet') {
        this.fadeOut();
        this.gap = -1;
      } else if (current && !this.decks[this.active]!.el.paused && this.fits(current, mood)) {
        // The song that's on suits the new place too: let it play out.
        this.decks[this.active]!.el.loop = mood === 'title';
      } else {
        this.gap = 0;
      }
    }
    if (this.gap >= 0 && this.mood && this.mood !== 'quiet') {
      this.gap -= dt;
      if (this.gap <= 0) {
        this.gap = -1;
        this.playTrack(this.shuffler.next(this.mood), this.mood === 'title');
      }
    }
  }

  /** Whether the song that's playing can carry on when the mood changes. */
  private fits(track: TrackId, mood: Exclude<Mood, 'quiet'>): boolean {
    if (mood === 'title') return track === 'title';
    // The swamp song stays in the swamp, and the swamp gets the swamp song.
    if (track === 'crick-water' || mood === 'backForty')
      return track === 'crick-water' && mood === 'backForty';
    // Walk into the Rusty Spur and the jukebox takes over, unless it's already one of its records.
    if (mood === 'bar') return PLAYLISTS.bar.includes(track);
    // Otherwise let it finish: dusk falling mid-song, or walking out of the bar humming.
    return true;
  }

  private playTrack(id: TrackId, loop: boolean): void {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    const old = this.decks[this.active];
    this.active = (this.active + 1) % this.decks.length;
    const d = this.decks[this.active]!;
    d.track = id;
    d.el.loop = loop;
    d.el.src = `${BASE}music/${id}.mp3`;
    d.el.currentTime = 0;
    d.gain.gain.cancelScheduledValues(now);
    d.gain.gain.setValueAtTime(0, now);
    d.gain.gain.linearRampToValueAtTime(1, now + 0.4);
    void d.el.play().catch(() => {
      // Blocked (no gesture yet) or the file is missing: try again in a bit.
      d.track = null;
      this.gap = 4;
    });
    if (old && old !== d) this.fadeDeck(old);
  }

  private fadeOut(): void {
    const d = this.decks[this.active];
    if (d) this.fadeDeck(d);
  }

  private fadeDeck(d: Deck): void {
    const now = this.ctx!.currentTime;
    d.gain.gain.cancelScheduledValues(now);
    d.gain.gain.setValueAtTime(d.gain.gain.value, now);
    d.gain.gain.linearRampToValueAtTime(0, now + MUSIC_FADE);
    const el = d.el;
    window.setTimeout(
      () => {
        if (d.gain.gain.value < 0.01) el.pause();
      },
      MUSIC_FADE * 1000 + 100,
    );
    d.track = null;
  }

  /** Feed the jukebox a dollar: the next bar song, right now. Returns its title. */
  jukebox(): string {
    const id = this.shuffler.next('bar');
    if (this.ctx && this.musicOn) {
      this.mood = 'bar';
      this.gap = -1;
      this.playTrack(id, false);
    }
    return TRACKS[id].title;
  }
}

/** The part of a decoded MP3 that isn't the encoder's leading or trailing silence. */
function audibleSpan(buffer: AudioBuffer): { start: number; end: number } {
  const data = buffer.getChannelData(0);
  const n = data.length;
  let a = 0;
  while (a < n && Math.abs(data[a] as number) < 1e-4) a++;
  let b = n - 1;
  while (b > a && Math.abs(data[b] as number) < 1e-4) b--;
  if (a >= b) return { start: 0, end: buffer.duration };
  return { start: a / buffer.sampleRate, end: (b + 1) / buffer.sampleRate };
}
